import { useMemo } from 'react';
import type { InBodyResult, PhysiologicalResult } from './model';
import { SegmentalAnalysisBody, DisplayMode } from '@/components/blocks/InBodyComposition';
import MetricCard from '@/components/common/MetricCard';
import Section from '@/components/common/Section';

interface PhysiologicalFitnessSectionProps {
    physiologicalFitness: PhysiologicalResult[];
    optimizeForPdf: boolean;
}

const getGridColsClass = (moduleIdentifier: string): string => {
    switch (moduleIdentifier) {
        case 'inbody-composition':
            return 'sm:grid-cols-6';
        default:
            return 'sm:grid-cols-4';
    }
};

const PhysiologicalFitnessSection = ({ physiologicalFitness, optimizeForPdf }: PhysiologicalFitnessSectionProps) => {
    const renderModuleContent = useMemo(() => {
        const renderDefaultMetricsSection = (result: PhysiologicalResult) => (
            <div className={`pdf-avoid-break grid gap-3 ${getGridColsClass(result.moduleDefinitionIdentifier)}`}>
                {result.result.map(r => (
                    <MetricCard key={r.label} label={r.label} value={r.value} />
                ))}
            </div>
        );

        const renderInBodySection = (inbodyResult: InBodyResult) => (
            <>
                {renderDefaultMetricsSection(inbodyResult)}

                {inbodyResult.segmentalData && (
                    <div className="pdf-avoid-break mt-5">
                        <SegmentalAnalysisBody
                            rowData={inbodyResult.segmentalData}
                            displayMode={DisplayMode.Both}
                            optimizeForPdf={optimizeForPdf}
                        />
                    </div>
                )}
            </>
        );

        return (m: PhysiologicalResult) => {
            const moduleIdentifier = m.moduleDefinitionIdentifier;

            switch (moduleIdentifier) {
                case 'inbody-composition':
                    return renderInBodySection(m as InBodyResult);
                default:
                    return renderDefaultMetricsSection(m);
            }
        };
    }, [optimizeForPdf]);

    return (
        <div className="space-y-5">
            {physiologicalFitness.map(m => (
                <Section
                    title={m.name}
                    subtitle={m.description}
                    key={m.moduleDefinitionIdentifier}
                    iconKey={m.moduleDefinitionIdentifier}
                    accent="purple"
                >
                    {renderModuleContent(m)}
                </Section>
            ))}
        </div>
    );
};

export default PhysiologicalFitnessSection;
